/**
 * Editor de persona (Brief 4 · Tela 2).
 *
 * Shell do editor: painel único com a estrutura de seções do gabarito (r5/
 * personas.app.jsx → PersonaEditor), espelhando o serializer real.
 *
 * Leitura (PR1/PR2) vs edição (PR3→PR5): o gate `persona-author` decide. SEM a
 * capability, a tela é idêntica ao PR2 (read-only, sem barra de ações) —
 * defense-in-depth: o gate está na AÇÃO (mutation + render), não só no botão.
 * COM a capability, TODAS as seções viram editáveis (Identidade PR3 · Vocabulário/
 * Limites éticos/Exemplos PR4 · Tom de voz/Comportamento PR5), com barra de ações
 * (Salvar/Descartar) e tratamento de conflito 409 (não sobrescreve cego). PR5
 * adiciona o ciclo de vida: **descontinuar** (DELETE soft → `deprecated`, com
 * confirmação inline — sem `window.confirm`, que travaria a automação).
 *
 * O bloco "Preview/Teste" (gerar resposta) e a seção "Modo de operação" do
 * gabarito são OMITIDOS — sem backend para gerar respostas e modo é conceito de
 * Playbook, não de Persona (zero dado falso).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useCapability } from '../../hooks/use-capability';
import { ApiError } from '../../lib/api-client';

import { VERTICAL_LABELS, verticalLabel } from './persona-labels';
import { PersonaActionBar } from './PersonaActionBar';
import { SectionBehaviorEdit } from './sections/SectionBehaviorEdit';
import { SectionBehaviorRead } from './sections/SectionBehaviorRead';
import { SectionConductEdit, SectionConductRead } from './sections/SectionConduct';
import { SectionEthicsEdit } from './sections/SectionEthicsEdit';
import { SectionEthicsRead } from './sections/SectionEthicsRead';
import { SectionExamplesEdit } from './sections/SectionExamplesEdit';
import { SectionExamplesRead } from './sections/SectionExamplesRead';
import { SectionVocabularyEdit } from './sections/SectionVocabularyEdit';
import { SectionVocabularyRead } from './sections/SectionVocabularyRead';
import { SectionVoiceEdit } from './sections/SectionVoiceEdit';
import { SectionVoiceRead } from './sections/SectionVoiceRead';
import { usePersonaForm } from './use-persona-form';
import { useDeprecatePersona, useUpdatePersona } from './use-personas';

import type { PersonaDto, PersonaVertical } from './types';

const VERTICAL_ORDER: PersonaVertical[] = [
  'support',
  'commercial',
  'retention',
  'collections',
  'triage',
  'onboarding',
  'satisfaction_survey',
  'legal',
  'healthcare',
  'other',
];

function formatDate(iso: string): string {
  // Apenas formata a data ISO do serializer em PT-BR; sem fabricar valor.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function statusReadLabel(status: PersonaDto['status']): string {
  if (status === 'active') return 'Ativo';
  if (status === 'draft') return 'Rascunho';
  return 'Descontinuado';
}

export function PersonaEditorView({
  persona,
  onBack,
}: {
  persona: PersonaDto;
  onBack: () => void;
}): JSX.Element {
  const canAuthor = useCapability('persona-author');
  const queryClient = useQueryClient();
  const form = usePersonaForm(persona);
  const mutation = useUpdatePersona(persona.id);
  const deprecateMutation = useDeprecatePersona(persona.id);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ currentVersion: number | null } | null>(null);
  const [confirmingDeprecate, setConfirmingDeprecate] = useState(false);
  const isDeprecated = persona.status === 'deprecated';

  const handleDeprecate = (): void => {
    setErrorMessage(null);
    deprecateMutation.mutate(
      { expectedVersion: persona.version },
      {
        onError: (err) => {
          setConfirmingDeprecate(false);
          if (err instanceof ApiError && err.status === 409) {
            const body = err.body as { currentVersion?: number };
            setConflict({ currentVersion: body.currentVersion ?? null });
            return;
          }
          setErrorMessage('Não foi possível descontinuar. Tente novamente.');
        },
        onSuccess: () => {
          setConfirmingDeprecate(false);
        },
      },
    );
  };

  const handleSave = (): void => {
    const patch = form.buildPatch();
    if (patch === null) return;
    setErrorMessage(null);
    mutation.mutate(
      { expectedVersion: persona.version, body: patch },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            const body = err.body as { currentVersion?: number };
            setConflict({ currentVersion: body.currentVersion ?? null });
            return;
          }
          setErrorMessage('Não foi possível salvar. Tente novamente.');
        },
        onSuccess: () => {
          setConflict(null);
          setErrorMessage(null);
        },
      },
    );
  };

  const handleDiscard = (): void => {
    form.reset();
    setErrorMessage(null);
    setConflict(null);
  };

  const handleReloadConflict = (): void => {
    setConflict(null);
    setErrorMessage(null);
    void queryClient.invalidateQueries({ queryKey: ['persona-detail', persona.id] });
  };

  return (
    <div className="personas-screen">
      <div className="pe-pagehead">
        <div className="pe-pagehead__heading">
          <div className="pe-pagehead__crumbs">Personas</div>
          <div className="pe-pagehead__title">
            <span className="pe-pagehead__name">{persona.name}</span>
          </div>
          <div className="pe-pagehead__sub">
            v{persona.version} · {canAuthor ? 'edição' : 'somente leitura'}
          </div>
        </div>
        <div className="pe-pagehead__actions">
          {canAuthor && !isDeprecated && (
            <button
              type="button"
              className="pe-btn pe-btn--danger-ghost"
              onClick={() => {
                setConfirmingDeprecate(true);
              }}
            >
              Descontinuar
            </button>
          )}
          <button type="button" className="pe-btn pe-btn--ghost" onClick={onBack}>
            ← Voltar à lista
          </button>
        </div>
      </div>

      {isDeprecated && (
        <div className="pe-note pe-note--warning" role="note">
          Esta persona está <strong>descontinuada</strong>. Ela permanece para preservar referências
          de playbooks, mas não deve ser usada em novas conversas.
        </div>
      )}

      {canAuthor && confirmingDeprecate && (
        <div
          className="pe-conflict-banner"
          role="alertdialog"
          aria-label="Confirmar descontinuação"
        >
          <span>
            Descontinuar <strong>{persona.name}</strong>? A persona vira{' '}
            <strong>somente histórico</strong> (soft-delete · preserva referências) e{' '}
            <strong>não há reativar</strong>
            pela tela.
          </span>
          <div className="pe-actionbar__buttons">
            <button
              type="button"
              className="pe-btn pe-btn--sm pe-btn--ghost"
              disabled={deprecateMutation.isPending}
              onClick={() => {
                setConfirmingDeprecate(false);
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="pe-btn pe-btn--sm pe-btn--danger"
              disabled={deprecateMutation.isPending}
              onClick={handleDeprecate}
            >
              {deprecateMutation.isPending ? 'Descontinuando…' : 'Confirmar descontinuação'}
            </button>
          </div>
        </div>
      )}

      {/* Erro fora do fluxo de edição (ex.: descontinuar) — a ActionBar só exibe erro de save */}
      {errorMessage !== null && !form.isDirty && !mutation.isPending && (
        <p className="pe-alert" role="alert">
          {errorMessage}
        </p>
      )}

      {canAuthor && conflict !== null && (
        <div className="pe-conflict-banner" role="alert">
          <span>
            Esta persona mudou desde que você abriu
            {conflict.currentVersion !== null
              ? ` (versão atual: v${conflict.currentVersion.toString()})`
              : ''}
            . Recarregue para ver a versão mais recente antes de editar — suas alterações não foram
            salvas.
          </span>
          <button type="button" className="pe-btn pe-btn--sm" onClick={handleReloadConflict}>
            Recarregar
          </button>
        </div>
      )}

      <div className="pe-editor-layout">
        <div className="pe-editor-main">
          {/* Seção 1 — Identidade (leitura PR1 · edição PR3 sob `persona-author`) */}
          <section className="pe-panel">
            <h2 className="pe-panel__title">Identidade</h2>
            <div className="pe-panel__section">
              <div className="pe-grid-2">
                <div className="pe-field">
                  <label className="pe-field__label" htmlFor="pe-name">
                    Nome
                  </label>
                  {canAuthor ? (
                    <input
                      id="pe-name"
                      type="text"
                      className="pe-input"
                      value={form.draft.name}
                      onChange={(e) => {
                        form.set('name', e.target.value);
                      }}
                    />
                  ) : (
                    <p className="pe-read-value">{persona.name}</p>
                  )}
                </div>
                <div className="pe-field">
                  <label className="pe-field__label" htmlFor="pe-vertical">
                    Vertical
                  </label>
                  {canAuthor ? (
                    <select
                      id="pe-vertical"
                      className="pe-select"
                      value={form.draft.vertical}
                      onChange={(e) => {
                        form.set('vertical', e.target.value as PersonaVertical);
                      }}
                    >
                      {VERTICAL_ORDER.map((v) => (
                        <option key={v} value={v}>
                          {VERTICAL_LABELS[v]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="pe-read-value">{verticalLabel(persona.vertical)}</p>
                  )}
                </div>
                <div className="pe-field">
                  <span className="pe-field__label">Idioma</span>
                  <p className="pe-read-value pe-read-value--mono">{persona.language}</p>
                </div>
                <div className="pe-field">
                  <span className="pe-field__label">Status</span>
                  <p className="pe-read-value">{statusReadLabel(persona.status)}</p>
                </div>
              </div>
            </div>
            <div className="pe-panel__divider" />
            <div className="pe-meta-row">
              <span>versão: {persona.version}</span>
              <span>criada: {formatDate(persona.createdAt)}</span>
              {persona.updatedAt !== null && (
                <span>atualizada: {formatDate(persona.updatedAt)}</span>
              )}
            </div>
          </section>

          {/* Seções 2-6 — edição sob `persona-author` (PR4/PR5), senão leitura (PR2) */}
          {canAuthor ? (
            <>
              <SectionVoiceEdit form={form} />
              <SectionVocabularyEdit form={form} />
              <SectionEthicsEdit form={form} />
              <SectionExamplesEdit form={form} />
              <SectionBehaviorEdit form={form} />
              {/* Fase 2 «Conduta e Contexto» — nível MAIS ESPECÍFICO da cascata. */}
              <SectionConductEdit persona={persona} form={form} />
            </>
          ) : (
            <>
              <SectionVoiceRead voice={persona.voiceProfile} />
              <SectionVocabularyRead vocabulary={persona.vocabulary} />
              <SectionEthicsRead ethics={persona.ethicalBoundaries} />
              <SectionExamplesRead examples={persona.examples} />
              <SectionBehaviorRead
                side={persona.sideDefault}
                adversarial={persona.adversarialProfileDefault}
              />
              {/* Fase 2 «Conduta e Contexto» — nível MAIS ESPECÍFICO da cascata. */}
              <SectionConductRead persona={persona} />
            </>
          )}
        </div>
      </div>

      {canAuthor && (
        <PersonaActionBar
          isDirty={form.isDirty}
          isSaving={mutation.isPending}
          errorMessage={errorMessage}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}
    </div>
  );
}
